import noop from 'lodash/noop';
import { useCallback, MouseEvent } from 'react';
import { Box } from '@/UI/Box/Box';
import { GridViewer } from '@/components/GridViewer/GridViewer';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { FileViewer } from '@/components/Nodes/Shared/FileViewer';
import GalleryMenu from './GalleryMenu';
import { GalleryFileViewer } from './GalleryFileViewer';
import { useImageList, type ImageListState } from './useImageList';
import { useImageActions, type ImageActions } from './useImageActions';
import { ImageListContainer, ImageListHeader, ImageListWrapper } from './ImageListComponents';
import { DesignAppCarousel } from './DesignAppCarousel';
import type { ThreeDProps, DeleteFunctions, ImageListContainerType } from './types';
import type { MenuAction } from '@/components/Menu/Actions';
import type { UploadedAsset } from '@/types/api/assets';
import type { SxProps } from '@/UI/theme.exports';
import type { NodeId } from 'web';

export type IndexedUploadedAsset = UploadedAsset & { globalIndex: number };

type ImageListProps = {
  container: ImageListContainerType;
  deletionFunctions?: DeleteFunctions;
  disabled?: boolean;
  images: IndexedUploadedAsset[];
  allImages?: IndexedUploadedAsset[];
  isHovered?: boolean;
  nodeName: string;
  onTextChange?: (index: number, newValue: string) => void;
  selected: number;
  setSelected: React.Dispatch<React.SetStateAction<number>>;
  showHeader?: boolean;
  threeDProps?: ThreeDProps;
  // grid view props
  fillHeight?: boolean;
  viewMode?: NodeViewMode;
  nodeId?: NodeId;
  shouldUseLegacyFileViewer?: boolean;
  isCameraLocked?: boolean;
  on3DLockStateChange?: (locked: boolean) => void;
  additionalContextMenuItems?: MenuAction[];
};

type ImageViewerWrapperProps = {
  children?: React.ReactNode;
  container: ImageListContainerType;
  isDesignApp?: boolean;
  fillHeight?: boolean;
  images: IndexedUploadedAsset[];
  isHovered?: boolean;
  onTextChange?: (index: number, newValue: string) => void;
  selected: number;
  setSelected: React.Dispatch<React.SetStateAction<number>>;
  sx?: SxProps;
  threeDProps?: ThreeDProps;
  // props for grid view
  viewMode: NodeViewMode;
  nodeId?: NodeId;
  shouldUseLegacyFileViewer?: boolean;
  isCameraLocked?: boolean;
  on3DLockStateChange?: (locked: boolean) => void;
  additionalContextMenuItems?: MenuAction[];
} & Pick<
  ImageListState,
  | 'contextMenu'
  | 'handleContextMenu'
  | 'handleContextMenuClose'
  | 'handleImageLoad'
  | 'handleSetAsCover'
  | 'imgRef'
  | 'maxSteps'
  | 'resetTransformRef'
  | 'rightClickedIndex'
  | 'selectedFile'
  | 'setScale'
  | 'transformKey'
  | 'videoRefs'
> &
  ImageActions;

const ImageViewerWrapper = ({
  children,
  container,
  isDesignApp = false,
  contextMenu,
  fillHeight,
  handleContextMenu,
  handleContextMenuClose,
  handleCopyImage,
  handleDownload,
  handleDownloadAll,
  handleImageLoad,
  handleSetAsCover,
  images,
  imgRef,
  isHovered,
  maxSteps,
  onTextChange,
  resetTransformRef,
  rightClickedIndex,
  selected,
  selectedFile,
  setSelected,
  setScale,
  sx,
  threeDProps,
  transformKey,
  videoRefs,
  // New props for grid view
  viewMode,
  nodeId,
  shouldUseLegacyFileViewer = false,
  additionalContextMenuItems,
}: ImageViewerWrapperProps) => {
  const {
    cameraPosition = undefined,
    setCameraPosition = noop,
    is3DLocked = false,
    setIs3DLocked = noop,
  } = threeDProps || {};
  const isGridViewMode = viewMode !== NodeViewMode.Single;
  // // ref is using for zooming purposes in the single file view mode, not relevant in grid view
  const imgContainerRef = isGridViewMode ? undefined : imgRef;

  const handleSingleContextMenu = useCallback(
    (e: MouseEvent<HTMLElement>) => handleContextMenu(e, selected),
    [handleContextMenu, selected],
  );

  return (
    <Box
      data-testid="image-list-wrapper"
      ref={imgContainerRef}
      sx={{
        ...sx,
        marginTop: 0,
        marginBottom: 0,
        width: viewMode === NodeViewMode.Single ? sx?.['width'] : '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(!isDesignApp && { flex: 1, minHeight: 0 }),
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {viewMode === NodeViewMode.Single ? (
        // single file view mode
        !shouldUseLegacyFileViewer && nodeId ? (
          <FileViewer
            id={nodeId}
            index={selected}
            asset={selectedFile}
            hasMultipleFiles={maxSteps > 1}
            handleContextMenu={handleSingleContextMenu}
            onTextChange={onTextChange}
          />
        ) : (
          <>
            {images.map((file) => (
              <GalleryFileViewer
                cameraPosition={cameraPosition}
                container={container}
                file={file}
                handleImageLoad={handleImageLoad}
                index={file.globalIndex}
                is3DLocked={is3DLocked}
                isHovered={isHovered}
                key={file.globalIndex}
                maxSteps={maxSteps}
                onContextMenu={handleContextMenu}
                onTextChange={onTextChange}
                resetTransformRef={resetTransformRef}
                selected={selected}
                setCameraPosition={setCameraPosition}
                setIs3DLocked={setIs3DLocked}
                setScale={setScale}
                transformKey={transformKey}
                videoRef={videoRefs[file.globalIndex]}
                fixedItemSize={file.type === 'audio' ? 150 : undefined}
              />
            ))}
          </>
        )
      ) : (
        <GridViewer
          nodeId={nodeId!}
          files={images}
          selectedIndex={selected}
          onFileSelect={setSelected}
          fillHeight={fillHeight}
          shouldUseLegacyFileViewer={shouldUseLegacyFileViewer}
          // props for legacy GalleryFileViewer
          threeDProps={threeDProps}
          maxSteps={maxSteps}
          onContextMenu={handleContextMenu}
          onTextChange={onTextChange}
          videoRefs={videoRefs}
        />
      )}
      <GalleryMenu
        contextMenu={contextMenu}
        handleContextMenuClose={handleContextMenuClose}
        handleCopyImage={handleCopyImage}
        handleDownload={handleDownload}
        handleDownloadAll={handleDownloadAll}
        handleSetAsCover={handleSetAsCover}
        rightClickedIndex={rightClickedIndex}
        selectedFile={selectedFile}
        additionalMenuItems={additionalContextMenuItems}
      />
      {children}
    </Box>
  );
};

export const ImageList = (props: ImageListProps) => {
  const {
    container,
    deletionFunctions,
    disabled,
    allImages,
    images,
    isHovered,
    nodeName,
    onTextChange,
    selected,
    showHeader,
    threeDProps,
    setSelected,
    // grid view props
    fillHeight,
    viewMode = NodeViewMode.Single,
    nodeId,
    shouldUseLegacyFileViewer = false,
    additionalContextMenuItems,
  } = props;

  const {
    contextMenu,
    handleContextMenu,
    handleContextMenuClose,
    handleImageLoad,
    handleSetAsCover,
    imagesPerRow,
    imgRef,
    isSelectedFileText,
    maxSteps,
    resetTransformRef,
    rightClickedIndex,
    selectedFile,
    setScale,
    transformKey,
    videoRefs,
    zoomPercentage,
  } = useImageList({ container, disabled, images: allImages ?? images, selected, setSelected });

  const { handleDownload, handleDownloadAll, handleCopyImage } = useImageActions({
    handleContextMenuClose,
    images,
    nodeName,
  });

  const imageListContainerWidthProperty = threeDProps && shouldUseLegacyFileViewer ? 'auto' : '100%';
  const isDesignApp = container === 'design-app';

  if (!Array.isArray(images)) {
    return null;
  }

  return (
    <ImageListWrapper>
      <ImageListContainer fullHeight={isDesignApp ? maxSteps < 2 : true}>
        {showHeader && (
          <ImageListHeader
            container={container}
            deletionFunctions={deletionFunctions}
            handleCopyImage={handleCopyImage}
            handleDownload={handleDownload}
            handleDownloadAll={handleDownloadAll}
            isSelectedFileText={isSelectedFileText}
            selected={selected}
            selectedFile={selectedFile}
            zoomPercentage={zoomPercentage}
          />
        )}
        <ImageViewerWrapper
          sx={{
            maxWidth: isDesignApp ? 'none' : 600,
            width: isDesignApp ? '90%' : imageListContainerWidthProperty,
            height: isDesignApp ? '85%' : 'auto',
            margin: 'auto',
            position: 'relative',
          }}
          container={container}
          isDesignApp={isDesignApp}
          contextMenu={contextMenu}
          handleContextMenu={handleContextMenu}
          handleContextMenuClose={handleContextMenuClose}
          handleCopyImage={handleCopyImage}
          handleDownload={handleDownload}
          handleDownloadAll={handleDownloadAll}
          handleImageLoad={handleImageLoad}
          handleSetAsCover={handleSetAsCover}
          images={images}
          imgRef={imgRef}
          isHovered={isHovered}
          maxSteps={maxSteps}
          onTextChange={onTextChange}
          resetTransformRef={resetTransformRef}
          rightClickedIndex={rightClickedIndex}
          selected={selected}
          selectedFile={selectedFile}
          setScale={setScale}
          threeDProps={threeDProps}
          transformKey={transformKey}
          videoRefs={videoRefs}
          setSelected={setSelected}
          // grid view props
          fillHeight={fillHeight}
          viewMode={viewMode}
          nodeId={nodeId}
          shouldUseLegacyFileViewer={shouldUseLegacyFileViewer}
          additionalContextMenuItems={additionalContextMenuItems}
        />
      </ImageListContainer>
      {isDesignApp && (
        <DesignAppCarousel
          handleContextMenu={handleContextMenu}
          images={images}
          imagesPerRow={imagesPerRow}
          maxSteps={maxSteps}
          selected={selected}
          setSelected={setSelected}
        />
      )}
    </ImageListWrapper>
  );
};
